(ns practitest.frontend.integration-settings.integration-field-mapping-form
  (:require [re-frame.core :as re-frame]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.utils :as utils]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.selector2 :as selector2]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.components2.spinners :as spinners]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.init :as init]
            [practitest.frontend.integration-settings.integration-field-mapping.events :as events]
            [practitest.frontend.integration-settings.integration-field-mapping.subs :as subs]))

(defn- used-ex-ids [rows]
  (set (map :ex-field-id rows)))

(defn- taken-map-to-values [rows current-row]
  (set (filter (complement #{"" "none" "description"})
               (map :map-to (remove #(= % current-row) rows)))))

(defn- compatible-pt-fields [pt-fields valid-pairs jira-pt-type]
  (if (= jira-pt-type "")
    pt-fields
    (filter (fn [f]
              (some #(= % [(:type f) jira-pt-type]) valid-pairs))
            pt-fields)))

(defn- taken-item-renderer
  [{:keys [taken item label-fn selected? hover? inner-ref on-hover on-change] :as _props}]
  (let [disabled? (contains? taken (:id item))]
    [:div.selector-item
     { :class         (dom/classes-pairs :active selected?
                                         :hover (and hover? (not disabled?))
                                         :disabled disabled?)
       :ref           inner-ref
       :on-mouse-over on-hover
       :on-mouse-move on-hover
       :on-click      (when-not disabled?
                        (fn [_] (when on-change (on-change item))))}
     [:span.selector-item-text
      { :class (dom/classes-pairs :active selected?)}
      (label-fn item)]]))

(defn- ex-field-select [idx row rows ex-fields bugtracker-name]
  (let [taken   (disj (used-ex-ids rows) (:ex-field-id row))
        current (first (filter #(= (:id %) (:ex-field-id row)) ex-fields))]
    [selector2/new-select
     { :value                  current
       :items                  ex-fields
       :key-fn                 :id
       :label-fn               :name
       :placeholder            (format-translation (get-translation "integration-field-mapping.select-external-field" "Select %{bugtracker} Field") :bugtracker bugtracker-name)
       :taken                  taken
       :strategy               "fixed"
       :selector-item-renderer taken-item-renderer
       :on-change              (fn [item]
                                 (re-frame/dispatch [::events/set-external-field idx (:id item)]))}]))

(defn- map-to-select [idx row rows pt-fields valid-pairs ex-fields]
  (let [ex-field     (first (filter #(= (:id %) (:ex-field-id row)) ex-fields))
        jira-pt-type (:pt-type ex-field "")
        allowed-pt   (compatible-pt-fields pt-fields valid-pairs jira-pt-type)
        taken        (taken-map-to-values rows row)
        items        (concat [{:id "none"        :label (get-translation "integration-field-mapping.select-pt-field.placeholder" "Select PractiTest Field")}
                               {:id "description" :label (get-translation "integration-field-mapping.description"
                                                                          "Description")}]
                             allowed-pt)
        current      (first (filter #(= (:id %) (:map-to row "none")) items))]
    [selector2/new-select
     { :value                  current
       :items                  items
       :key-fn                 :id
       :label-fn               :label
       :taken                  taken
       :strategy               "fixed"
       :selector-item-renderer taken-item-renderer
       :on-change              (fn [item]
                                 (re-frame/dispatch [::events/set-map-to idx (:id item)]))}]))

(defn- entity-checkbox-disabled? [row pt-fields entity]
  (let [map-to   (:map-to row "none")
        pt-field (first (filter #(= (:id %) map-to) pt-fields))
        entities (get pt-field :entities [])]
    (events/entity-blocked? map-to entities entity)))

(defn- entity-not-linked-title [row pt-fields entity]
  (let [map-to   (:map-to row "none")
        pt-field (when (not (#{  "none" "" "description"} map-to))
                   (first (filter #(= (:id %) map-to) pt-fields)))
        entities (get pt-field :entities [])]
    (when (and (some? pt-field) (not (some #(= % entity) entities)))
      (format-translation
        (get-translation "integration-field-mapping.entity-not-linked"
                         "The %{field} field is not linked to %{entity} entity in PractiTest.")
        :field (:label pt-field "") :entity entity))))

(defn- checkbox-with-tooltip [tooltip-msg input]
  (if tooltip-msg
    [tooltip/mux-tooltip {:position "auto"} [:span tooltip-msg]
     (fn [props] [:span props input])]
    input))

(defn- table-row [idx row rows ex-fields pt-fields valid-pairs bugtracker-name]
  (let [issues-disabled? (entity-checkbox-disabled? row pt-fields "Issue")
        reqs-disabled?   (entity-checkbox-disabled? row pt-fields "Requirement")]
    ^{:key idx}
    [:tr
     [:td [ex-field-select idx row rows ex-fields bugtracker-name]]
     [:td [map-to-select idx row rows pt-fields valid-pairs ex-fields]]
     [:td.text-center
      [:div.d-flex.justify-content-center
       [:input.m-checkbox
        { :type      "checkbox"
          :checked   (:fail-and-issue row false)
          :on-change #(re-frame/dispatch [::events/set-row-field idx :fail-and-issue (-> % .-target .-checked)])}]]]
     [:td.text-center
      [:div.d-flex.justify-content-center
       [checkbox-with-tooltip (entity-not-linked-title row pt-fields "Issue")
        [:input.m-checkbox
         { :type      "checkbox"
           :checked   (and (:for-issues row false) (not issues-disabled?))
           :disabled  issues-disabled?
           :on-change #(re-frame/dispatch [::events/set-row-field idx :for-issues (-> % .-target .-checked)])}]]]]
     [:td.text-center
      [:div.d-flex.justify-content-center
       [checkbox-with-tooltip (entity-not-linked-title row pt-fields "Requirement")
        [:input.m-checkbox
         { :type      "checkbox"
           :checked   (and (:for-requirements row false) (not reqs-disabled?))
           :disabled  reqs-disabled?
           :on-change #(re-frame/dispatch [::events/set-row-field idx :for-requirements (-> % .-target .-checked)])}]]]]
     [:td.text-center.align-middle
      [buttons/icon-button
       { :icon     [icons/mux-trash]
         :on-click #(re-frame/dispatch [::events/remove-row idx])}]]]))

(defn integration-field-mapping-form [_]
  (fn [_]
    (let [initialized?     @(re-frame/subscribe [::subs/initialized?])
          rows             @(re-frame/subscribe [::subs/rows])
          ex-fields        @(re-frame/subscribe [::subs/ex-fields])
          pt-fields        @(re-frame/subscribe [::subs/pt-fields])
          valid-pairs      @(re-frame/subscribe [::subs/valid-pairs])
          saving?          @(re-frame/subscribe [::subs/saving?])
          changes-made?    @(re-frame/subscribe [::subs/changes-made?])
          bugtracker-name  @(re-frame/subscribe [::subs/bugtracker-name])]
      (if-not initialized?
        [:div.integration-field-mapping-loading
         [spinners/mux-spinner {:delay 200}]]
        [:div.integration-field-mapping-table
         [:div.d-flex.justify-content-end.mb-3
          [buttons/button
           { :text     (get-translation "integration-field-mapping.add-new-mapping" "Add New Mapping")
             :primary? true
             :on-click #(re-frame/dispatch [::events/add-row])}]]

       [:div.table-container
        [:table.table
         [:thead
          [:tr
           [:th (format-translation (get-translation "integration-field-mapping.external-field" "%{bugtracker} Field Name") :bugtracker bugtracker-name)]
           [:th (get-translation "integration-field-mapping.map-to" "Map To")]
           [:th.text-center (get-translation "integration-field-mapping.fail-and-issue" "Add Field to Fail and Issue")]
           [:th.text-center (get-translation "integration-field-mapping.add-to-issues" "Add to Issues")]
           [:th.text-center (get-translation "integration-field-mapping.add-to-requirements" "Add to Requirements")]
           [:th ""]]]
         [:tbody
          (doall
            (map-indexed
              (fn [idx row]
                (table-row idx row rows ex-fields pt-fields valid-pairs bugtracker-name))
              rows))]]]

         [:div.d-flex.align-items-center.gap-2.mt-3
          [buttons/button
           { :text     (get-translation "generic.button.cancel" "Cancel")
             :on-click #(re-frame/dispatch [::events/cancel])}]
          [buttons/button
           { :text      (if saving?
                          (get-translation "integration-field-mapping.saving" "Saving...")
                          (get-translation "integration-field-mapping.update-settings" "Update Settings"))
             :primary?  (boolean changes-made?)
             :disabled? (or saving? (not changes-made?))
             :on-click  #(re-frame/dispatch [::events/save])}]]]))))

(defn ^:dev/after-load remount []
  (init/remount-roots "integration-field-mapping-component"
                      integration-field-mapping-form))

(defn ^:export init [webcomponent options]
  (init/init! { :options       options
                :init-event-fn (fn [config]
                                 [::events/initialize-db config])
                :webcomponent  webcomponent
                :root-view     integration-field-mapping-form}))

(utils/register-webcomponent! "integration-field-mapping-component")
